import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ContentLoader } from '@/components/ui';

/**
 * Loading context state interface
 */
export interface LoadingState {
  /** Whether loading is active */
  isLoading: boolean;
  /** Loading message */
  message: string;
  /** Loading size */
  size: 'sm' | 'md' | 'lg';
}

/**
 * Loading context actions interface
 */
export interface LoadingActions {
  /** Show loading overlay */
  showLoading: (message?: string, size?: 'sm' | 'md' | 'lg') => void;
  /** Hide loading overlay */
  hideLoading: () => void;
}

/**
 * Loading context type
 */
export type LoadingContextType = LoadingState & LoadingActions;

/**
 * Create loading context
 */
const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

/**
 * Loading provider props
 */
interface LoadingProviderProps {
  children: ReactNode;
}

/**
 * Loading provider component
 * Manages content loading state for the application
 */
export function LoadingProvider({ children }: LoadingProviderProps) {
  const [state, setState] = useState<LoadingState>({
    isLoading: false,
    message: 'Loading...',
    size: 'md',
  });

  const showLoading = useCallback((message?: string, size?: 'sm' | 'md' | 'lg') => {
    setState({
      isLoading: true,
      message: message || 'Loading...',
      size: size || 'md',
    });
  }, []);

  const hideLoading = useCallback(() => {
    setState(prev => ({ ...prev, isLoading: false }));
  }, []);

  const contextValue: LoadingContextType = useMemo(() => ({
    ...state,
    showLoading,
    hideLoading,
  }), [state, showLoading, hideLoading]);

  return (
    <LoadingContext.Provider value={contextValue}>
      {children}
    </LoadingContext.Provider>
  );
}

/**
 * Hook to use loading context
 * @returns Loading context value
 * @throws Error if used outside LoadingProvider
 */
export function useLoading(): LoadingContextType {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
}

/**
 * Hook to use loading context safely (no error if outside provider)
 * @returns Loading context value or default state
 */
export function useLoadingSafe(): LoadingContextType {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    // Return default state if context is not available
    return {
      isLoading: false,
      message: 'Loading...',
      size: 'md',
      showLoading: () => {},
      hideLoading: () => {},
    };
  }
  return context;
}

/**
 * Hook to use content loading with ContentLoader component
 * @returns Loading state and ContentLoader component
 */
export function useContentLoading() {
  const { isLoading, message, size, showLoading, hideLoading } = useLoading();

  const executeWithLoading = useCallback(async <T,>(
    operation: () => Promise<T>,
    msg?: string,
    sz?: 'sm' | 'md' | 'lg'
  ): Promise<T> => {
    showLoading(msg, sz);
    try {
      const result = await operation();
      return result;
    } finally {
      // Add a small delay to ensure the loader is visible, then hide
      setTimeout(() => {
        hideLoading();
      }, 300);
    }
  }, [showLoading, hideLoading]);

  const ContentLoaderComponent = useCallback(() => (
    <ContentLoader
      isVisible={isLoading}
      message={message}
      size={size}
    />
  ), [isLoading, message, size]);

  return {
    isLoading,
    message,
    size,
    showLoading,
    hideLoading,
    executeWithLoading,
    ContentLoader: ContentLoaderComponent,
  };
}

/**
 * Hook to execute async operations with loading state (legacy)
 * @param operation Async function to execute
 * @param message Loading message
 * @param size Loading spinner size
 * @returns Promise with operation result
 */
export function useLoadingOperation() {
  const { executeWithLoading } = useContentLoading();
  return { executeWithLoading };
}